import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Typography,
  Box,
  Avatar,
  useTheme,
  alpha,
  Skeleton,
  Stack,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Star as StarIcon,
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  Bed as BedIcon,
  CalendarToday as CalendarTodayIcon,
  MonetizationOn as MonetizationOnIcon,
  Build as BuildIcon,
  PushPin as PushPinIcon,
  PlayArrow as PlayArrowIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '@utils/financialUtils';
import { Hotel } from '../../../types/types';

interface HotelTableProps {
  hotels: Hotel[];
  loading?: boolean;
  pinnedProperties?: Set<string>;
  onTogglePin?: (hotelId: string) => void;
  sortBy?: string;
  currency?: 'USD' | 'EUR';
}

const HotelTable: React.FC<HotelTableProps> = ({
  hotels,
  loading = false,
  pinnedProperties = new Set(),
  onTogglePin,
  sortBy = 'lastUpdated',
  currency = 'USD'
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleViewHotel = (hotelId: string) => {
    navigate(`/hotels/${hotelId}`);
  };

  const handleEditHotel = (hotelId: string) => {
    navigate(`/hotels/${hotelId}/edit`);
  };

  const formatLocation = (hotel: Hotel) => {
    if (hotel.city && hotel.state) {
      return `${hotel.city}, ${hotel.state}`;
    }
    return hotel.address || 'Location not specified';
  };

  const getTotalRenovationCost = (hotel: Hotel) => {
    if (!hotel.renovation_history?.length) return 0;
    return hotel.renovation_history.reduce((sum, renovation) => sum + renovation.cost, 0);
  };

  const getPropertyAge = (hotel: Hotel) => {
    if (!hotel.year_built) return null;
    const currentYear = new Date().getFullYear();
    return currentYear - hotel.year_built;
  };

  const getStatusInfo = (hotel: Hotel) => {
    // This would typically come from actual status data
    return {
      label: 'Active',
      color: 'success' as const,
      variant: 'filled' as const
    };
  };

  const LoadingRow = () => (
    <TableRow sx={{ height: 80 }}>
      <TableCell sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Skeleton variant="circular" width={48} height={48} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" height={24} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="50%" height={16} />
          </Box>
        </Box>
      </TableCell>
      {!isMobile && (
        <>
          <TableCell><Skeleton variant="text" width="80%" height={20} /></TableCell>
          <TableCell><Skeleton variant="text" width="60%" height={20} /></TableCell>
          <TableCell><Skeleton variant="text" width="40%" height={20} /></TableCell>
          <TableCell><Skeleton variant="text" width="60%" height={20} /></TableCell>
          <TableCell><Skeleton variant="text" width="40%" height={20} /></TableCell>
          <TableCell><Skeleton variant="text" width="80%" height={20} /></TableCell>
          <TableCell><Skeleton variant="text" width="70%" height={20} /></TableCell>
          <TableCell><Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} /></TableCell>
        </>
      )}
      <TableCell>
        <Stack direction="row" spacing={1}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="circular" width={32} height={32} />
        </Stack>
      </TableCell>
    </TableRow>
  );

  // Mobile-optimized columns
  const mobileColumns = ['Hotel', 'Key Info', 'Actions'];
  const desktopColumns = [
    'Hotel', 'Location', 'Type', 'Rooms', 'Rating', 'Built', 'Value', 'Renovations', 'Status', 'Actions'
  ];

  const columns = isMobile ? mobileColumns : desktopColumns;

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden'
      }}
    >
      <Table sx={{ minWidth: isMobile ? 'auto' : 900 }}>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
              '& .MuiTableCell-head': {
                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                py: 1.5
              }
            }}
          >
            {columns.map((column) => (
              <TableCell
                key={column}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  color: theme.palette.text.primary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            [...Array(5)].map((_, index) => <LoadingRow key={index} />)
          ) : hotels.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                    }}
                  >
                    <BusinessIcon sx={{ fontSize: 24 }} />
                  </Avatar>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    No Properties Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
                    No hotels match your current criteria. Try adjusting your filters or add a new property to get started.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            hotels.map((hotel) => {
              const roomCount = hotel.num_rooms || 0;
              const value = hotel.estimated_value || 0;
              const renovationCost = getTotalRenovationCost(hotel);
              const propertyAge = getPropertyAge(hotel);
              const status = getStatusInfo(hotel);

              return (
                <TableRow
                  key={hotel.id}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    },
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      py: 1.5
                    }
                  }}
                  onClick={() => handleViewHotel(hotel.id!)}
                >
                  {/* Hotel Name - Always visible */}
                  <TableCell sx={{ minWidth: isMobile ? 200 : 250 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          fontSize: 14,
                          fontWeight: 600,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                        }}
                      >
                        {hotel.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            lineHeight: 1.3,
                            color: theme.palette.text.primary,
                            mb: 0.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {hotel.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <BusinessIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {hotel.property_type || 'Hotel'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>

                  {isMobile ? (
                    /* Mobile: Key Info Column */
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationOnIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {formatLocation(hotel)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <BedIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {roomCount}
                            </Typography>
                          </Box>
                          {value > 0 && (
                            <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                              {formatCurrency(value, currency)}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={status.label}
                          color={status.color}
                          variant={status.variant}
                          size="small"
                          sx={{ alignSelf: 'flex-start', fontWeight: 600, height: 20 }}
                        />
                      </Stack>
                    </TableCell>
                  ) : (
                    /* Desktop: Individual Columns */
                    <>
                      {/* Location */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatLocation(hotel)}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Type */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {hotel.property_type || 'Hotel'}
                        </Typography>
                      </TableCell>

                      {/* Rooms */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <BedIcon sx={{ fontSize: 14, color: theme.palette.primary.main }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {roomCount.toLocaleString()}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Value */}
                      <TableCell>
                        {value > 0 ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <MonetizationOnIcon sx={{ fontSize: 14, color: theme.palette.success.main }} />
                            <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                              {formatCurrency(value, currency)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not valued
                          </Typography>
                        )}
                      </TableCell>

                      {/* Renovations */}
                      <TableCell>
                        {renovationCost > 0 ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <BuildIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatCurrency(renovationCost, currency)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={status.label}
                          color={status.color}
                          variant={status.variant}
                          size="small"
                          sx={{ fontWeight: 600, height: 24 }}
                        />
                      </TableCell>
                    </>
                  )}

                  {/* Actions - Always visible */}
                  <TableCell sx={{ width: 140 }}>
                    <Stack direction="row" spacing={0.5}>
                      {onTogglePin && (
                        <Tooltip title={pinnedProperties.has(hotel.id!) ? "Unpin Property" : "Pin Property"}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onTogglePin(hotel.id!);
                            }}
                            sx={{
                              backgroundColor: pinnedProperties.has(hotel.id!)
                                ? alpha(theme.palette.warning.main, 0.1)
                                : alpha(theme.palette.text.secondary, 0.1),
                              color: pinnedProperties.has(hotel.id!)
                                ? theme.palette.warning.main
                                : theme.palette.text.secondary,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.warning.main, 0.2),
                                color: theme.palette.warning.main
                              },
                              p: 0.5
                            }}
                          >
                            <PushPinIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="Run Valuation">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Run valuation action
                          }}
                          sx={{
                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                            color: theme.palette.success.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.success.main, 0.2),
                            },
                            p: 0.5
                          }}
                        >
                          <PlayArrowIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewHotel(hotel.id!);
                          }}
                          sx={{
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.2),
                            },
                            p: 0.5
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Edit Hotel">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditHotel(hotel.id!);
                          }}
                          sx={{
                            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                            color: theme.palette.secondary.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                            },
                            p: 0.5
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default HotelTable;